/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { DOMNode, domToReact, Element, HTMLReactParserOptions, Text } from "html-react-parser";

import { DescriptionStyleMap } from "./descriptionStyleMap";

/**
 * Returns true when every meaningful (non-whitespace) child of a <p> element
 * is a single <strong> element — i.e. the paragraph is a section heading per
 * the HTML Usage Expectations standard.
 *
 * Legacy content such as <p><span style="..."><strong>…</strong></span></p>
 * does NOT match (the <strong> is not a direct child) and falls back to the
 * normal paragraph renderer, preserving the inline style during transition.
 */
const isHeadingParagraph = (element: Element): boolean => {
  const meaningfulChildren = element.children.filter(
    (child): child is DOMNode => !(child.type === "text" && (child as Text).data.trim() === "")
  );

  return (
    meaningfulChildren.length === 1 &&
    meaningfulChildren[0] instanceof Element &&
    meaningfulChildren[0].name === "strong"
  );
};

/**
 * Builds an html-react-parser `replace` options object from a DescriptionStyleMap.
 *
 * Each recognised HTML structure is mapped to the corresponding style-map renderer.
 * Unknown or out-of-standard tags (including legacy wrappers like <div>) return
 * undefined, which causes html-react-parser to fall back to default rendering —
 * children are still processed recursively through the same options.
 *
 * This mirrors the pattern used in src/pages/Maintenance.tsx.
 */
export const createReplaceOptions = (styleMap: DescriptionStyleMap): HTMLReactParserOptions => {
  const options: HTMLReactParserOptions = {
    replace(domNode) {
      if (!(domNode instanceof Element)) {
        return;
      }

      const children = domToReact(domNode.children as DOMNode[], options);

      switch (domNode.name) {
        case "p":
          return isHeadingParagraph(domNode)
            ? styleMap.heading(children)
            : styleMap.paragraph(children);

        case "ul":
          return styleMap.list(children);

        case "li":
          return styleMap.listItem(children);

        case "table":
          return styleMap.table(children);

        case "thead":
          return styleMap.tableHead(children);

        case "tbody":
          return styleMap.tableBody(children);

        case "tr":
          return styleMap.row(children);

        case "th":
          return styleMap.headerCell(children);

        case "td":
          return styleMap.dataCell(children);

        // <div> wrapper and any other tag: fall through to default rendering.
        // Children are still processed recursively by html-react-parser.
        default:
          return;
      }
    },
  };

  return options;
};
